import { TableColumn } from "@/components/ui/Table";

/**
 * 인쇄 옵션
 */
export interface PrintOptions {
  /** 문서 제목 */
  title?: string;
  /** 문서 방향 (가로/세로) */
  orientation?: "portrait" | "landscape";
  /** 그룹 헤더 포함 여부 */
  includeGroupHeader?: boolean;
}

/**
 * 오늘 날짜를 YYYY-MM-DD 형식으로 반환
 */
const getFormattedDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * 컬럼 정의에서 헤더 추출
 */
const getHeader = (column: TableColumn, includeGroup: boolean): string => {
  if (includeGroup && column.group) {
    return `${column.group}<br/>${column.header}`;
  }
  return column.header;
};

/**
 * 데이터 값을 출력에 적합한 형태로 변환
 */
const getValue = (
  value: any,
  row: any,
  index: number,
  column: TableColumn
): string => {
  // render 함수가 있는 경우
  if (column.render) {
    const rendered = column.render(value, row, index);
    // React 엘리먼트인 경우 원본 값 사용
    if (typeof rendered === "object" && rendered !== null) {
      if (typeof value === "number") {
        return value.toLocaleString();
      }
      return String(value ?? "");
    }
    return String(rendered ?? "");
  }

  // 값이 없는 경우
  if (value === null || value === undefined) {
    return "";
  }

  // 숫자인 경우 포맷팅
  if (typeof value === "number") {
    return value.toLocaleString();
  }

  return String(value);
};

/**
 * 테이블 데이터를 새 창에서 인쇄
 * 브라우저의 기본 인쇄 기능을 사용하여 한글 완벽 지원
 *
 * @param data - 테이블 데이터 배열
 * @param columns - 테이블 컬럼 정의 배열
 * @param options - 인쇄 옵션
 *
 * @example
 * printTable(tableData, columns, { title: "매출현황" });
 */
export const printTable = <T extends Record<string, any>>(
  data: T[],
  columns: TableColumn[],
  options: PrintOptions = {}
): void => {
  const {
    title,
    orientation = "landscape",
    includeGroupHeader = true,
  } = options;

  // 내부 메타데이터 컬럼 제외
  const exportColumns = columns.filter(
    col => !col.key.startsWith("__") && col.key !== "no"
  );

  // 데이터 필터링
  const filteredData = data.filter(row => {
    if ("__rowType" in row && row.__rowType === "summary") {
      return false;
    }
    return true;
  });

  // 컬럼 너비 계산
  const colWidths = exportColumns.map(col => {
    const widthStr = col.width || "100px";
    return parseInt(widthStr.replace("px", ""), 10) || 100;
  });
  const totalWidth = colWidths.reduce((sum, w) => sum + w, 0);

  // HTML 생성
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${title || "인쇄"}</title>
      <style>
        @page {
          size: ${orientation === "landscape" ? "A4 landscape" : "A4 portrait"};
          margin: 10mm;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Malgun Gothic', '맑은 고딕', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
          font-size: 10px;
          line-height: 1.4;
          color: #333;
        }

        .container {
          padding: 10px;
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          padding-bottom: 10px;
          border-bottom: 2px solid #333;
        }

        .title {
          font-size: 18px;
          font-weight: bold;
        }

        .date {
          font-size: 11px;
          color: #666;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 9px;
        }

        th, td {
          border: 1px solid #ddd;
          padding: 5px 6px;
          vertical-align: middle;
        }

        th {
          background-color: #424242;
          color: white;
          font-weight: 600;
          text-align: center;
          white-space: nowrap;
        }

        td {
          background-color: white;
        }

        tr:nth-child(even) td {
          background-color: #f8f8f8;
        }

        .text-left { text-align: left; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }

        .row-number {
          width: 30px;
          text-align: center;
          color: #666;
        }

        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <div class="title">${title || ""}</div>
          <div class="date">${getFormattedDate()}</div>
        </div>

        <table>
          <thead>
            <tr>
              <th class="row-number">No</th>
              ${exportColumns
                .map((col, idx) => {
                  const widthPercent = ((colWidths[idx] / totalWidth) * 100).toFixed(1);
                  return `<th style="width: ${widthPercent}%">${getHeader(col, includeGroupHeader)}</th>`;
                })
                .join("")}
            </tr>
          </thead>
          <tbody>
            ${filteredData
              .map((row, rowIndex) => `
                <tr>
                  <td class="row-number">${rowIndex + 1}</td>
                  ${exportColumns
                    .map(col => {
                      const align = col.bodyAlign || "left";
                      const value = getValue(row[col.key], row, rowIndex, col);
                      return `<td class="text-${align}">${value}</td>`;
                    })
                    .join("")}
                </tr>
              `)
              .join("")}
          </tbody>
        </table>
      </div>

      <script>
        window.onload = function() {
          window.print();
        };
      </script>
    </body>
    </html>
  `;

  // 새 창에서 인쇄
  const printWindow = window.open("", "_blank");
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  }
};

export default printTable;
